import { collection, getDoc, doc, addDoc, query, where, getDocs, runTransaction } from 'firebase/firestore';
import { db } from '../App';

export const fetchInventoryItems = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userInventory = userDoc.data().inventory || [];
    const itemsData = await Promise.all(
      userInventory.map(async (itemId) => {
        const originalItemId = itemId.split('_').slice(-1)[0];
        const itemDocRef = doc(db, 'items', originalItemId);
        const itemDocSnap = await getDoc(itemDocRef);
        if (itemDocSnap.exists()) {
          return { id: itemId, ...itemDocSnap.data() };
        } else {
          console.error(`Item with id ${originalItemId} does not exist`);
          return null;
        }
      })
    );
    return itemsData.filter(item => item !== null);
  } catch (error) {
    console.error('Error fetching inventory items:', error);
    return [];
  }
};

export const getTradeIdOrCreate = async (fromUserId, toUserId) => {
  try {
    const tradesRef = collection(db, 'trades');
    const q1 = query(
      tradesRef, 
      where('from', '==', fromUserId), 
      where('to', '==', toUserId), 
      where('status', 'not-in', ['completed', 'cancelled'])
    );
    const q2 = query(
      tradesRef, 
      where('from', '==', toUserId), 
      where('to', '==', fromUserId), 
      where('status', 'not-in', ['completed', 'cancelled'])
    );
    const querySnapshot1 = await getDocs(q1);
    const querySnapshot2 = await getDocs(q2);

    let existingTrade = null;

    if (!querySnapshot1.empty) {
      querySnapshot1.forEach((doc) => {
        existingTrade = doc.id;
      });
    }

    if (!querySnapshot2.empty) {
      querySnapshot2.forEach((doc) => {
        existingTrade = doc.id;
      });
    }

    if (existingTrade) {
      return existingTrade;
    }

    const newTrade = {
      from: fromUserId,
      to: toUserId,
      fromItems: [],
      toItems: [],
      status: 'fromselect',
      timestamp: new Date(),
    };
    const docRef = await addDoc(tradesRef, newTrade);
    return docRef.id;
  } catch (error) {
    console.error('Error getting or creating trade:', error);
    return null;
  }
};

export const addItemsToTrade = async (tradeId, userId, items) => {
  try {
    const tradeRef = doc(db, 'trades', tradeId);
    const userRef = doc(db, 'users', userId);

    await runTransaction(db, async (transaction) => {
      const tradeDoc = await transaction.get(tradeRef);
      const userDoc = await transaction.get(userRef);

      if (!tradeDoc.exists() || !userDoc.exists()) {
        throw new Error('Trade or user not found');
      }

      const tradeData = tradeDoc.data();
      const userData = userDoc.data();

      const newItems = items.map(item => item.id);
      const updatedInventory = userData.inventory.filter(itemId => !newItems.includes(itemId));

      if (tradeData.from === userId) {
        transaction.update(tradeRef, {
          fromItems: newItems,
          status: 'toselect'
        });
      } else if (tradeData.to === userId) {
        transaction.update(tradeRef, {
          toItems: newItems,
          status: 'pending'
        });
      } else {
        throw new Error('User is not part of this trade');
      }

      transaction.update(userRef, { inventory: updatedInventory });
    });

    return true;
  } catch (error) {
    console.error('Error adding items to trade:', error);
    return false;
  }
};

export const completeTrade = async (tradeId) => {
  try {
    const tradeRef = doc(db, 'trades', tradeId);
    const tradeDoc = await getDoc(tradeRef);
    if (!tradeDoc.exists()) {
      throw new Error('Trade not found');
    }
    const tradeData = tradeDoc.data();

    const fromUserRef = doc(db, 'users', tradeData.from);
    const toUserRef = doc(db, 'users', tradeData.to);

    await runTransaction(db, async (transaction) => {
      const fromUserDoc = await transaction.get(fromUserRef);
      const toUserDoc = await transaction.get(toUserRef);

      if (!fromUserDoc.exists() || !toUserDoc.exists()) {
        throw new Error('One or both users not found');
      }

      const fromUserData = fromUserDoc.data();
      const toUserData = toUserDoc.data();

      // Update inventories
      const updatedFromInventory = [...fromUserData.inventory, ...tradeData.toItems];
      const updatedToInventory = [...toUserData.inventory, ...tradeData.fromItems];

      // Perform the updates
      transaction.update(fromUserRef, { inventory: updatedFromInventory });
      transaction.update(toUserRef, { inventory: updatedToInventory });
      transaction.update(tradeRef, { status: 'completed' });
    });

    return true;
  } catch (error) {
    console.error('Error completing trade:', error);
    return false;
  }
};

export const rejectTrade = async (tradeId) => {
  try {
    const tradeRef = doc(db, 'trades', tradeId);
    const tradeDoc = await getDoc(tradeRef);
    if (!tradeDoc.exists()) {
      throw new Error('Trade not found');
    }
    const tradeData = tradeDoc.data();

    const fromUserRef = doc(db, 'users', tradeData.from);
    const toUserRef = doc(db, 'users', tradeData.to);

    await runTransaction(db, async (transaction) => {
      const fromUserDoc = await transaction.get(fromUserRef);
      const toUserDoc = await transaction.get(toUserRef);

      if (!fromUserDoc.exists() || !toUserDoc.exists()) {
        throw new Error('One or both users not found');
      }

      const fromUserData = fromUserDoc.data();
      const toUserData = toUserDoc.data();

      // Return items to original inventories
      const updatedFromInventory = [...fromUserData.inventory, ...tradeData.fromItems];
      const updatedToInventory = [...toUserData.inventory, ...tradeData.toItems];

      transaction.update(fromUserRef, { inventory: updatedFromInventory });
      transaction.update(toUserRef, { inventory: updatedToInventory });
      transaction.update(tradeRef, { status: 'cancelled' }); // Changed from 'rejected' to 'cancelled'
    });

    return true;
  } catch (error) {
    console.error('Error rejecting trade:', error);
    return false;
  }
};

export const cancelTrade = async (tradeId, userId) => {
  try {
    const tradeRef = doc(db, 'trades', tradeId);
    
    return await runTransaction(db, async (transaction) => {
      const tradeDoc = await transaction.get(tradeRef);
      if (!tradeDoc.exists()) {
        throw new Error('Trade not found');
      }
      
      const tradeData = tradeDoc.data();
      
      // Check if the trade is already cancelled
      if (tradeData.status === 'cancelled') {
        return { success: true, message: 'Trade was already cancelled' };
      }
      
      const fromUserRef = doc(db, 'users', tradeData.from);
      const toUserRef = doc(db, 'users', tradeData.to);
      
      const fromUserDoc = await transaction.get(fromUserRef);
      const toUserDoc = await transaction.get(toUserRef);
      
      if (!fromUserDoc.exists() || !toUserDoc.exists()) {
        throw new Error('One or both users not found');
      }
      
      const fromUserData = fromUserDoc.data();
      const toUserData = toUserDoc.data();
      
      // Check if the cancelling user has any of the trade items in their inventory
      const userInventory = userId === tradeData.from ? fromUserData.inventory : toUserData.inventory;
      const tradeItems = [...tradeData.fromItems, ...tradeData.toItems];
      
      if (tradeItems.some(item => userInventory.includes(item))) {
        return { success: false, message: 'Cannot cancel trade: Some items are already in your inventory' };
      }
      
      // Return items to original inventories
      const updatedFromInventory = [...fromUserData.inventory, ...tradeData.fromItems];
      const updatedToInventory = [...toUserData.inventory, ...tradeData.toItems];
      
      transaction.update(fromUserRef, { inventory: updatedFromInventory });
      transaction.update(toUserRef, { inventory: updatedToInventory });
      transaction.update(tradeRef, { status: 'cancelled' });
      
      return { success: true, message: 'Trade cancelled successfully' };
    });
  } catch (error) {
    console.error('Error cancelling trade:', error);
    return { success: false, message: error.message };
  }
};